import { useEffect, useMemo, useRef, useState } from "react";
import { formatPct, formatPrice } from "../utils/format";
import { deriveAlertType, extractAlertPct, parseImpulseMessage, windowLabelFromType } from "../utils/alertClassifier";
import { coinbaseSpotUrl } from "../utils/coinbaseUrl";
import { normalizeAlert, typeKeyToUpper } from "../utils/alerts_normalize";
import { stripLeadingSymbol, stripLeadingType } from "../utils/alertText";
import { createEventKey, shouldSkip, markSeen, expireOldKeys } from "../utils/intelligenceDedup";

const safeSymbol = (value) => {
  if (!value) return "";
  const v = String(value);
  return v.replace(/-USD$|-USDT$|-PERP$/i, "").toUpperCase();
};

const toNum = (value) => {
  if (value == null) return NaN;
  if (typeof value === "number") return value;
  const normalized = Number(String(value).replace(/[%+]/g, ""));
  return Number.isFinite(normalized) ? normalized : NaN;
};

const pickNumber = (...values) => {
  for (const v of values) {
    const n = toNum(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const formatDeltaPct = (raw) => {
  if (!Number.isFinite(raw)) return null;
  const pct = Math.abs(raw) <= 1 ? raw * 100 : raw;
  return formatPct(pct, { sign: true });
};

const extractDivergenceRaw = (obj) =>
  obj?.google_trend_score ??
  obj?.google_trend_delta ??
  obj?.trend_score ??
  obj?.trend_delta ??
  obj?.divergence_score ??
  obj?.divergenceScore ??
  obj?.divergence ??
  obj?.divergence_value ??
  obj?.divergenceValue ??
  obj?.search_trend ??
  obj?.searchTrend ??
  obj?.divergence_analysis ??
  null;

const formatDivergence = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "object") {
    const type = raw.divergence_type ?? raw.type ?? raw.label ?? null;
    const magnitude = pickNumber(raw.magnitude, raw.score, raw.value);
    if (type && Number.isFinite(magnitude)) return `Divergence ${type} ${magnitude.toFixed(2)}`;
    if (type) return `Divergence ${type}`;
    if (Number.isFinite(magnitude)) {
      const display = Math.abs(magnitude) <= 1 ? `${(magnitude * 100).toFixed(1)}%` : magnitude.toFixed(2);
      return `Divergence ${display}`;
    }
  }
  const n = toNum(raw);
  if (Number.isFinite(n)) {
    const display = Math.abs(n) <= 1 ? `${(n * 100).toFixed(1)}%` : n.toFixed(2);
    return `Divergence ${display}`;
  }
  const s = String(raw).trim();
  return s ? `Divergence ${s}` : null;
};

const classifyLog = (log) => {
  if (log?.label) {
    return { label: log.label, chipTone: log.chipTone || "info" };
  }
  const norm = normalizeAlert(log);
  const extracted = extractAlertPct(log);
  const pct = extracted.pct ?? (Number.isFinite(norm?.pct) ? norm.pct : toNum(log?.pct));
  const derivedType = deriveAlertType({
    type: log?.type ?? norm?.type,
    pct,
    severity: log?.severity ?? norm?.severity ?? log?.sev,
  }) || typeKeyToUpper(norm.type_key);
  let chipTone = "info";
  if (["MOONSHOT", "BREAKOUT", "IMPULSE", "FOMO"].includes(derivedType)) chipTone = "gain";
  else if (["CRATER", "DUMP", "FEAR"].includes(derivedType)) chipTone = "loss";
  else if (derivedType === "MOVE") chipTone = Number.isFinite(pct) && pct < 0 ? "loss" : "gain";
  else if (["SENTIMENT", "DIVERGENCE", "VOLUME", "WHALE", "STEALTH"].includes(derivedType)) chipTone = "sent";
  return { label: derivedType, chipTone };
};

const cleanAlertMessage = (message, symbol, typeLabel) => {
  let text = String(message || "").replace(/\s+/g, " ").trim();
  if (!text) return "";

  const sym = String(symbol || "").trim().toUpperCase();
  const type = String(typeLabel || "").trim();

  text = stripLeadingSymbol(text, sym);
  text = stripLeadingType(text, type);

  return text;
};

const buildMessage = (log, label) => {
  let base = String(log?.body || log?.msg || "").trim();
  base = base
    .replace(/\bIMPULSE[_\s-]?1M\b/gi, "")
    .replace(/\bIMPULSE[_\s-]?3M\b/gi, "")
    .replace(/\s*—\s*—?/g, " — ")
    .replace(/\s{2,}/g, " ")
    .replace(/^\s*—\s*/, "")
    .replace(/\s*—\s*$/, "")
    .trim();
  const pct = Number.isFinite(log?.pct) ? log.pct : toNum(log?.pct);
  const win = log?.window || log?.window_label || windowLabelFromType(log?.type) || "";
  const pctText = Number.isFinite(pct) ? `${formatPct(pct, { sign: true })}${win ? ` in ${win}` : ""}` : null;

  const volPct = pickNumber(
    log?.vol_change_pct,
    log?.vol_pct,
    log?.volume_change_1h_pct,
    log?.volume_change_pct,
    log?.volumeChangePct
  );
  const volText = Number.isFinite(volPct) ? `Vol ${volPct > 0 ? "+" : ""}${volPct.toFixed(0)}%` : null;

  const sentimentDelta = pickNumber(
    log?.sentiment_delta,
    log?.sentimentDelta,
    log?.sentiment_change,
    log?.sentimentChange,
    log?.sentiment_score_delta,
    log?.sentimentScoreDelta
  );
  const sentimentText = Number.isFinite(sentimentDelta) ? `Sent ${formatDeltaPct(sentimentDelta)}` : null;

  const priceNow = pickNumber(log?.price_now, log?.price, log?.current_price);
  const priceThen = pickNumber(log?.price_then, log?.initial_price);
  let priceText = null;
  if (Number.isFinite(priceNow)) {
    const nowText = formatPrice(priceNow);
    priceText = Number.isFinite(priceThen) ? `${nowText} from ${formatPrice(priceThen)}` : nowText;
  }

  const divRaw = log?.divergence ?? log?.divergence_raw ?? extractDivergenceRaw(log);
  const divergenceText = formatDivergence(divRaw);

  const extras = [];
  if (pctText && !base.includes("%")) extras.push(pctText);
  if (volText && !base.includes("%")) extras.push(volText);
  if (sentimentText && !base.includes("%")) extras.push(sentimentText);
  if (priceText && !base.includes("$")) extras.push(priceText);
  if (divergenceText) extras.push(divergenceText);
  else if (label === "DIVERGENCE") extras.push("Divergence: unavailable");

  if (base) return extras.length ? `${base} · ${extras.join(" · ")}` : base;
  return extras.join(" · ");
};

const renderIntelMessage = (message) => {
  if (!message) return null;
  const text = String(message);
  const parts = [];
  const regex = /([+-]\d+(?:\.\d+)?%|\$\d[\d,]*(?:\.\d+)?)/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text))) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const token = match[1];
    if (token.startsWith("$")) {
      parts.push(
        <span key={`price-${match.index}`} className="bh-intel-price">
          {token}
        </span>
      );
    } else {
      const cls = token.startsWith("-") ? "bh-intel-pct bh-intel-pct--neg" : "bh-intel-pct bh-intel-pct--pos";
      parts.push(
        <span key={`pct-${match.index}`} className={cls}>
          {token}
        </span>
      );
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
};

const spotUrl = (token, symbol) => {
  const productId =
    token?.product_id ||
    token?.symbol ||
    token?.ticker ||
    (symbol ? `${symbol}-USD` : null);
  return coinbaseSpotUrl({ product_id: productId, symbol });
};

const getAlertTradeUrl = (log) => {
  const u = log?.url;
  if (typeof u === "string" && u.startsWith("http")) return u;

  const pid = (log?.product_id || log?.symbol || "").toString().trim().toUpperCase();
  if (!pid) return null;
  const productId = pid.includes("-") ? pid : `${pid}-USD`;
  return `https://www.coinbase.com/advanced-trade/spot/${encodeURIComponent(productId)}`;
};

export default function AnomalyStream({ data = {}, volumeData = [] }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [logs, setLogs] = useState([
    { id: "init-1", time: "INIT", msg: "ESTABLISHING NEURAL LINK...", label: "SYSTEM" },
    { id: "init-2", time: "INIT", msg: "M3_COPROCESSOR: ONLINE", label: "SYSTEM" },
  ]);

  // Map<semanticKey, timestampMs> — TTL-based dedup; expires after two cooldown cycles
  const seenRef = useRef(new Map());
  // Map<symbol, lastFiredMs> — per-symbol cooldown for the volume gap-filler
  const volCooldownRef = useRef(new Map());
  const scrollRef = useRef(null);

  // 1-second ticker drives relative timestamps and fresh-row detection
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!isCollapsed && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs, isCollapsed]);

  const visibleLogs = isCollapsed ? [] : logs;

  // Rows inserted within the last 3s get the fresh-entry animation
  const freshIds = useMemo(() => {
    const s = new Set();
    for (const l of logs) {
      if (l.ts && nowMs - l.ts < 3000) s.add(l.id);
    }
    return s;
  }, [logs, nowMs]);

  // Relative "updated X ago" label shown in the header
  const relLabel = useMemo(() => {
    let latest = 0;
    for (const l of logs) { if (l.ts && l.ts > latest) latest = l.ts; }
    if (!latest) return null;
    const diff = Math.floor((nowMs - latest) / 1000);
    if (diff < 5)  return "just now";
    if (diff < 60) return `${diff}s ago`;
    return `${Math.floor(diff / 60)}m ago`;
  }, [logs, nowMs]);

  const vol1h = useMemo(() => (Array.isArray(volumeData) ? volumeData : []), [volumeData]);
  const alerts = useMemo(() => (Array.isArray(data?.alerts) ? data.alerts : []), [data]);

  // Volume gap-filler: covers the 80%+ range the backend WHALE detector misses
  // when its per-minute z-score baseline is not yet warm (first ~15 min of uptime).
  // Runs independently of backend alert count \u2014 no guard.
  useEffect(() => {
    if (!vol1h.length) return;

    const nowMs = Date.now();
    const timeStr = new Date(nowMs)
      .toLocaleTimeString([], { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" })
      .replace(/\u200E/g, "");

    const newLogs = [];
    for (const token of vol1h) {
      const symbol = safeSymbol(token?.symbol || token?.ticker);
      const pct = pickNumber(token?.volume_change_1h_pct, token?.volume_change_pct, token?.volumeChangePct);
      if (!symbol || !Number.isFinite(pct) || pct < 80) continue;

      const lastFired = volCooldownRef.current.get(symbol) || 0;
      if (nowMs - lastFired < 5 * 60 * 1000) continue;

      const volRounded = Math.floor(pct);
      const url = spotUrl(token, symbol);
      newLogs.push({
        id: `vol-${symbol}-${nowMs}`,
        ts: nowMs,
        time: timeStr,
        source: "synthetic",
        label: "VOLUME",
        chipTone: "sent",
        symbol,
        url,
        prefix: "",
        body: `Vol +${volRounded}% above average`,
        vol_change_pct: volRounded,
        window: "1h",
        type: "VOLUME_1H",
        divergence: extractDivergenceRaw(token),
      });
      volCooldownRef.current.set(symbol, nowMs);
    }

    if (newLogs.length) {
      setLogs((prev) => [...prev, ...newLogs].slice(-60));
    }
  }, [vol1h]);

  useEffect(() => {
    if (!Array.isArray(alerts) || alerts.length === 0) return;

    const nowMs = Date.now();
    expireOldKeys(seenRef.current, nowMs);

    const timeStr = new Date(nowMs)
      .toLocaleTimeString([], { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" })
      .replace(/\u200E/g, "");

    const newLogs = [];
    for (const a of alerts) {
      if (!a) continue;
      // Semantic dedup: same type+symbol+window+direction = same ongoing condition
      const key = createEventKey(a);
      if (shouldSkip(key, seenRef.current, nowMs)) continue;

      const norm = normalizeAlert(a);
      const symbol = safeSymbol(norm.symbol || norm.product_id || a.symbol || a.product_id || a.pair);
      const url = a.trade_url || spotUrl(
        { product_id: norm.product_id || a.product_id || (symbol ? `${symbol}-USD` : null) },
        symbol
      );
      const extracted = extractAlertPct(a);
      const type = deriveAlertType({
        type: a?.type ?? norm?.type,
        pct: extracted.pct ?? norm?.pct,
        severity: a?.severity ?? norm?.severity ?? a?.sev,
      }) || typeKeyToUpper(norm.type_key);
      const parsed = parseImpulseMessage(a);
      const pct = extracted.pct ?? pickNumber(norm?.pct, a?.pct, parsed?.parsed_pct);
      const windowLabel = norm?.window || windowLabelFromType(a?.type) || parsed?.parsed_window_label || "";
      const volPct = pickNumber(a?.vol_change_pct, a?.vol_pct, a?.meta?.vol_change_pct, a?.meta?.vol_pct);
      const sentimentDelta = pickNumber(
        a?.sentiment_delta,
        a?.sentimentDelta,
        a?.meta?.sentiment_delta,
        a?.meta?.sentimentDelta
      );
      const divergenceRaw = extractDivergenceRaw(a) ?? extractDivergenceRaw(a?.meta || {});
      const rawMsg = a.message || a.title || "";
      const msg = cleanAlertMessage(rawMsg, symbol, type);

      newLogs.push({
        id: `a-${nowMs}-${Math.random().toString(36).slice(2, 6)}`,
        ts: nowMs,
        time: timeStr,
        source: "alert",
        symbol,
        url,
        prefix: "",
        body: msg || "",
        type: norm?.type || a?.type || type,
        type_key: norm?.type_key || "",
        severity: norm?.severity || a?.severity || "info",
        pct,
        metrics: a?.metrics || null,
        window: windowLabel,
        vol_change_pct: volPct,
        sentiment_delta: sentimentDelta,
        divergence: divergenceRaw,
      });
      markSeen(key, seenRef.current, nowMs);
    }

    if (newLogs.length) {
      setLogs((prev) => [...prev, ...newLogs].slice(-60));
    }
  }, [alerts]);

  return (
    <section className="bh-intel-panel" data-collapsed={isCollapsed ? "1" : "0"}>
      <div className="bh-intel-head">
        <button
          type="button"
          className="bh-intel-toggle"
          onClick={() => setIsCollapsed((v) => !v)}
          aria-expanded={!isCollapsed}
        >
          <div className="bh-intel-title">
            INTELLIGENCE_LOG
            <span className={`bh-intel-state ${isCollapsed ? "is-standby" : "is-live"}`}>
              {isCollapsed ? "STANDBY" : "LIVE"}
            </span>
          </div>
          <div className="bh-intel-sub">Live anomalies</div>
        </button>
        <div className="bh-intel-meta">v4.0.2</div>
        {relLabel ? <span className="bh-intel-updated">{relLabel}</span> : null}
      </div>

      <div
        ref={scrollRef}
        className={`bh-intel-log ${isCollapsed ? "is-collapsed" : ""}`}
        role="log"
        aria-live="polite"
      >
        {visibleLogs.map((log) => {
          const tradeUrl = getAlertTradeUrl(log);
          const isClickable = Boolean(tradeUrl);
          const { label, chipTone } = classifyLog(log);
          const chipClass = `bh-intel-chip bh-intel-chip--${chipTone}`;
          const message = buildMessage(log, label);
          const showPrefix = Boolean(log.prefix) && log.prefix !== "!!!";

          return (
            <div
              key={log.id}
              className={`bh-intel-line${isClickable ? " bh-intel-clickable" : ""}${freshIds.has(log.id) ? " is-fresh" : ""}`}
              role={isClickable ? "link" : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onClick={(e) => {
                if (!isClickable) return;
                const target = e.target;
                if (target && target.closest && target.closest("a")) return;
                window.open(tradeUrl, "_blank", "noreferrer");
              }}
              onKeyDown={(e) => {
                if (!isClickable) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  window.open(tradeUrl, "_blank", "noreferrer");
                }
              }}
            >
              <span className="bh-intel-ts">[{log.time}]</span>
              <span className={chipClass}>{label}</span>
              <span className="bh-intel-msg">
                {showPrefix ? <span className="bh-intel-prefix">{log.prefix}</span> : null}{" "}
                {log.symbol ? (
                  tradeUrl ? (
                    <a className="bh-intel-sym" href={tradeUrl} target="_blank" rel="noreferrer">
                      {log.symbol}
                    </a>
                  ) : (
                    <span className="bh-intel-sym">{log.symbol}</span>
                  )
                ) : null}{" "}
                <span className="bh-intel-text">{renderIntelMessage(message)}</span>
              </span>
            </div>
          );
        })}
        {!isCollapsed ? <div className="bh-intel-cursor">_</div> : null}
      </div>

      {!isCollapsed ? <div className="bh-intel-scan">scanning live anomalies</div> : null}
    </section>
  );
}
